import { useState } from 'react';
import { useClub } from '../app/club';
import { useRepo } from '../app/services';
import { useClubMutation } from '../data/hooks';
import type { Person } from '../lib/types';
import { EMAIL_RE, inviteErrorText, planBulkInvite, type AppStatus } from '../lib/appStatus';
import { Badge, Button, Dialog, Field, useToast } from './ui';

export function AppStatusBadge({ status }: { status: AppStatus }) {
  return <Badge tone={status.tone}>{status.label}</Badge>;
}

function GuardianCheckbox({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ width: 18, height: 18, marginTop: 3 }} />
      <span>{label}</span>
    </label>
  );
}

/** Eén persoon uitnodigen voor de app; e-mailadres kan eerst worden aangepast. */
export function InviteDialog({ person, status, onClose }: { person: Person; status: AppStatus; onClose: () => void }) {
  const { orgId } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const [email, setEmail] = useState(person.email ?? status.inviteEmail ?? '');
  const [guardian, setGuardian] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; guardian?: string; form?: string }>({});
  const send = useClubMutation(orgId, () =>
    repo.invitePerson({ person_id: person.id, email: email.trim().toLowerCase(), guardian_informed: person.is_minor && guardian }),
  );
  const again = status.kind === 'invited' || status.kind === 'expired';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: typeof errors = {};
    if (!EMAIL_RE.test(email.trim())) errs.email = 'Vul een geldig e-mailadres in.';
    if (person.is_minor && !guardian) errs.guardian = 'Bevestig dat de ouder of verzorger is geïnformeerd.';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    try {
      const r = await send.mutateAsync(undefined);
      toast(
        r.status === 'existing_user'
          ? `${person.full_name} heeft al een Antreja-account. De koppeling gebeurt zodra die de app opent.`
          : `Uitnodiging verstuurd naar ${r.email}.`,
      );
      onClose();
    } catch (err) {
      setErrors({ form: inviteErrorText(err) });
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={again ? 'Opnieuw uitnodigen' : 'Uitnodigen voor de app'}
      description={`${person.full_name} krijgt een e-mail. Daarna logt die in de Antreja-app in met dit e-mailadres en een code van 6 cijfers.`}
    >
      <form className="stack" onSubmit={submit} noValidate>
        <Field
          label="E-mailadres"
          hint={person.email && email.trim().toLowerCase() !== person.email.toLowerCase() ? 'Het nieuwe adres wordt ook bij de persoon opgeslagen.' : undefined}
          error={errors.email}
        >
          {(p) => <input {...p} className="input" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        {person.is_minor && (
          <div className="stack" style={{ gap: 4 }}>
            <GuardianCheckbox checked={guardian} onChange={setGuardian} label="Ouder/verzorger is geïnformeerd over de app en dit e-mailadres." />
            {errors.guardian && (
              <span className="error" role="alert">
                {errors.guardian}
              </span>
            )}
          </div>
        )}
        {again && <p className="muted small">De vorige uitnodiging vervalt.</p>}
        {errors.form && (
          <div className="alert alert-danger" role="alert">
            {errors.form}
          </div>
        )}
        <div className="dialog-actions">
          <Button onClick={onClose}>Annuleren</Button>
          <Button variant="primary" type="submit" disabled={send.isPending}>
            {send.isPending ? 'Versturen…' : 'Uitnodiging versturen'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

interface BulkResult {
  sent: number;
  existing: number;
  failed: { name: string; reason: string }[];
  stopped: string | null;
}

/** "Leden zonder app uitnodigen": iedereen in `candidates` (al gefilterd op geen app / verlopen uitnodiging). */
export function BulkInviteDialog({ candidates, context, onClose }: { candidates: Person[]; context: string; onClose: () => void }) {
  const { orgId } = useClub();
  const repo = useRepo();
  const [guardian, setGuardian] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);
  const plan = planBulkInvite(candidates, guardian);
  const minors = candidates.filter((p) => p.is_minor && p.email && EMAIL_RE.test(p.email.trim())).length;

  const run = useClubMutation(orgId, async (list: Person[]) => {
    const r: BulkResult = { sent: 0, existing: 0, failed: [], stopped: null };
    for (const [n, p] of list.entries()) {
      setProgress(n);
      try {
        const res = await repo.invitePerson({ person_id: p.id, email: null, guardian_informed: p.is_minor && guardian });
        if (res.status === 'existing_user') r.existing++;
        else r.sent++;
      } catch (err) {
        const reason = inviteErrorText(err);
        r.failed.push({ name: p.full_name, reason });
        if (/50 uitnodigingen/.test(reason)) {
          r.stopped = reason;
          break;
        }
      }
    }
    setProgress(null);
    return r;
  });

  return (
    <Dialog open onOpenChange={(o) => !o && progress === null && onClose()} title="Leden zonder app uitnodigen" description={context}>
      {result ? (
        <div className="stack">
          <p>
            {result.sent} uitnodiging{result.sent === 1 ? '' : 'en'} verstuurd.
            {result.existing > 0 && ` ${result.existing} ${result.existing === 1 ? 'persoon heeft' : 'personen hebben'} al een Antreja-account; die worden gekoppeld zodra ze de app openen.`}
          </p>
          {result.stopped && <div className="alert alert-warn">{result.stopped}</div>}
          {result.failed.length > 0 && (
            <div className="alert alert-danger" role="alert">
              Niet gelukt:
              <ul style={{ margin: '4px 0 0 18px' }}>
                {result.failed.slice(0, 10).map((f) => (
                  <li key={f.name}>
                    {f.name}: {f.reason}
                  </li>
                ))}
              </ul>
              {result.failed.length > 10 && `en nog ${result.failed.length - 10}.`}
            </div>
          )}
          <div className="dialog-actions">
            <Button variant="primary" onClick={onClose}>
              Sluiten
            </Button>
          </div>
        </div>
      ) : (
        <div className="stack">
          {candidates.length === 0 ? (
            <p>Iedereen heeft de app al of is al uitgenodigd.</p>
          ) : (
            <>
              <p>
                <strong>{plan.invite.length}</strong> {plan.invite.length === 1 ? 'persoon krijgt' : 'personen krijgen'} een e-mail met uitleg om in de Antreja-app in
                te loggen met hun e-mailadres en een code.
              </p>
              {plan.noEmail.length > 0 && (
                <p className="muted small">
                  {plan.noEmail.length} zonder (geldig) e-mailadres {plan.noEmail.length === 1 ? 'wordt' : 'worden'} overgeslagen. Nodig die los uit vanaf de persoonspagina.
                </p>
              )}
              {minors > 0 && (
                <GuardianCheckbox
                  checked={guardian}
                  onChange={setGuardian}
                  label={`Ook ${minors} minderjarige${minors === 1 ? '' : 'n'} uitnodigen: ouders/verzorgers zijn geïnformeerd.`}
                />
              )}
              <p className="muted small">Maximaal 50 uitnodigingen per uur per club.</p>
            </>
          )}
          {progress !== null && (
            <p role="status">
              Bezig: {progress + 1} van {plan.invite.length}…
            </p>
          )}
          <div className="dialog-actions">
            <Button onClick={onClose} disabled={progress !== null}>
              Annuleren
            </Button>
            <Button
              variant="primary"
              disabled={plan.invite.length === 0 || run.isPending}
              onClick={async () => setResult(await run.mutateAsync(plan.invite))}
            >
              {plan.invite.length === 1 ? '1 uitnodiging versturen' : `${plan.invite.length} uitnodigingen versturen`}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
